import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";
import { SignalFilterStatus } from "@/enum/list.enum";

// Signal processing status enum - matches backend TrackerSignalResultStatus
export enum TrackerSignalResultStatus {
	DRAFT = "DRAFT",
	PROCESSING = "PROCESSING",
	PENDING = "PENDING",
	COLLECTED = "COLLECTED",
	SCORED = "SCORED",
	COMPLETED = "COMPLETED",
	ERROR = "ERROR",
}

// Signal priority enum - matches backend signal priority values (e.g., "Prio 1", "Prio 2", etc.)
export enum TrackerSignalPriority {
	PRIORITY_1 = "Prio 1",
	PRIORITY_2 = "Prio 2",
	PRIORITY_3 = "Prio 3",
	DISQUALIFIED = "Disqualified",
}

// Raw signal schema
export const RawSignalSchema = z
	.object({
		title: z.string().nullable().optional(),
		summary: z.string().nullable().optional(),
		source_url: z.string().nullable().optional(),
		published_date: z.string().nullable().optional(),
		relevance_reasoning: z.string().nullable().optional(),
	})
	.nullable()
	.optional();

// Scored signal schema
export const ScoredSignalSchema = z
	.object({
		title: z.string().nullable().optional(),
		summary: z.string().nullable().optional(),
		priority: z.string().nullable().optional(),
		reasoning: z.string().nullable().optional(),
		source_url: z.string().nullable().optional(),
		published_date: z.string().nullable().optional(),
		signal_category: z.string().nullable().optional(),
	})
	.nullable()
	.optional();

export const LinkedinSignalActorSchema = z.object({
	actor_image: z.string().nullable().optional(),
	actor_name: z.string().nullable().optional(),
	actor_description: z.string().nullable().optional(),
	actor_navigationContext: z.string().nullable().optional(),
});

export const LinkedinSignalSocialDetailsSchema = z.object({
	numLikes: z.number().nullable().optional(),
	numComments: z.number().nullable().optional(),
	numShares: z.number().nullable().optional(),
});

export const LinkedinSignalMediaSchema = z.object({
	type: z.enum(["image", "video", "document", "article"]),
	url: z.string(),
	thumbnailUrl: z.string().nullable().optional(),
	title: z.string().nullable().optional(),
	subtitle: z.string().nullable().optional(),
	width: z.number().nullable().optional(),
	height: z.number().nullable().optional(),
});

export const LinkedinSignalResharedPostSchema = z.object({
	authorName: z.string().nullable().optional(),
	authorImage: z.string().nullable().optional(),
	authorHeadline: z.string().nullable().optional(),
	text: z.string().nullable().optional(),
	media: z.array(LinkedinSignalMediaSchema).nullable().optional(),
});

export const LinkedinSignalPostSchema = z.object({
	share_url: z.string().nullable().optional(),
	actor: LinkedinSignalActorSchema.nullable().optional(),
	commentary: z.string().nullable().optional(),
	social_details: LinkedinSignalSocialDetailsSchema.nullable().optional(),
	postedAt: z.union([z.string(), z.date()]).nullable().optional(),
	media: z.array(LinkedinSignalMediaSchema).nullable().optional(),
	resharedPost: LinkedinSignalResharedPostSchema.nullable().optional(),
});

// TrackerSignalResult schema - matches backend TrackerSignalResultDto
export const TrackerSignalResultSchema = z.object({
	id: z.number(),
	trackerOrganizationId: z.number().nullable(),
	trackerPersonId: z.number().nullable(),
	trackerId: z.number().nullable().optional(),
	listId: z.number().nullable().optional(),
	companyId: z.number().nullable().optional(),
	status: z.string(), // Using string to handle various status values
	rawSignal: RawSignalSchema,
	scoredSignal: ScoredSignalSchema,
	signalCategory: z.string().nullable().optional(),
	priority: z.string().nullable().optional(),
	title: z.string().nullable().optional(),
	summary: z.string().nullable().optional(),
	sourceUrl: z.string().nullable().optional(),
	publishedDate: z.string().nullable().optional(),
	reasoning: z.string().nullable().optional(),
	signalCollectedAt: z.string().nullable().optional(),
	signalScoredAt: z.string().nullable().optional(),
	errorMessage: z.string().nullable().optional(),
	linkedinPostUrn: z.string().nullable().optional(),
	linkedinPost: LinkedinSignalPostSchema.nullable().optional(),
	linkedinCommentary: z.string().nullable().optional(),
	createdAt: z.string().transform((arg) => new Date(arg)),
	updatedAt: z
		.string()
		.transform((arg) => new Date(arg))
		.optional(),
	// Using z.any() to break circular dependency:
	// trackerPerson.ts → get-people.ts → trackerSignalResult.ts → trackerOrganizations.ts → trackerPerson.ts
	trackerOrganization: z.any().optional(),
});

// Paginated response schema
export const TrackerSignalResultResponseSchema = z.object({
	data: z.array(TrackerSignalResultSchema),
	meta: MetaDataSchema,
});

/**
 * The timeline is a merged feed (PREC-2496): AI signals interleaved with the moments a lead — or,
 * on a company timeline, a colleague from the same company — turned up on a lead list.
 */
export const TimelineEntryTypeSchema = z.enum(["SIGNAL", "LIST_APPEARANCE"]);

export const ListAppearanceEntrySchema = z.object({
	listPersonId: z.number(),
	listId: z.number(),
	listName: z.string(),
	assignStatus: z.string().nullable().optional(),
	prospectId: z.number(),
	prospectName: z.string().nullable().optional(),
	prospectTitle: z.string().nullable().optional(),
	/** False when the appearance belongs to a colleague rather than the person this timeline is about. */
	isSelf: z.boolean(),
});

export const TimelineEntrySchema = z.object({
	type: TimelineEntryTypeSchema,
	occurredAt: z.string().nullable().optional(),
	signal: TrackerSignalResultSchema.optional(),
	listAppearance: ListAppearanceEntrySchema.optional(),
});

export const TimelineEntryResponseSchema = z.object({
	data: z.array(TimelineEntrySchema),
	meta: MetaDataSchema,
});

// IGNORE auto-disqualifies the signal server-side; SCORE_CHANGE stores the reason
// of a manual priority change without touching the priority (PREC-3635 D1).
export const TrackerSignalFeedbackActionSchema = z.enum(["IGNORE", "SCORE_CHANGE"]);

export const IgnoreTrackerSignalRequestSchema = z.object({
	action: TrackerSignalFeedbackActionSchema,
	feedback: z.string().trim().min(3),
	listPersonId: z.number().optional(),
	prospectId: z.number().optional(),
	trackerOrganizationId: z.number().optional(),
});

export const TrackerSignalFeedbackResponseSchema = z.object({
	id: z.number(),
	trackerSignalResultId: z.number(),
	action: TrackerSignalFeedbackActionSchema,
	feedback: z.string(),
	createdAt: z.string().transform((arg) => new Date(arg)),
});

// Types
export type TrackerSignalResult = z.infer<typeof TrackerSignalResultSchema>;
export type TrackerSignalResultResponse = z.infer<typeof TrackerSignalResultResponseSchema>;
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;
export type ListAppearanceEntry = z.infer<typeof ListAppearanceEntrySchema>;
export type TimelineEntryResponse = z.infer<typeof TimelineEntryResponseSchema>;
export type LinkedinSignalPost = z.infer<typeof LinkedinSignalPostSchema>;
export type LinkedinSignalMedia = z.infer<typeof LinkedinSignalMediaSchema>;
export type LinkedinSignalResharedPost = z.infer<typeof LinkedinSignalResharedPostSchema>;
export type IgnoreTrackerSignalRequest = z.infer<typeof IgnoreTrackerSignalRequestSchema>;
export type TrackerSignalFeedbackResponse = z.infer<typeof TrackerSignalFeedbackResponseSchema>;

// Request params for fetching signals
export interface TrackerSignalResultParams {
	trackerOrganizationId: number;
	page?: number;
	limit?: number;
	searchValue?: string;
	sortBy?: string;
	sortOrder?: "ASC" | "DESC";
	signalFilterStatus?: SignalFilterStatus[];
	/**
	 * Narrows the timeline's list appearances to one person while signals stay org-wide — sent by
	 * panels that show a single lead, so a colleague's list membership is never presented as theirs.
	 */
	appearanceProspectId?: number;
	/** Set false to get signals only — required by any caller reading a single "latest signal". */
	includeListAppearances?: boolean;
}

// Request params for fetching signals by prospect
export interface ProspectSignalResultParams {
	prospectId: number;
	page?: number;
	limit?: number;
	signalFilterStatus?: SignalFilterStatus[];
	/** Set false to get signals only — required by any caller reading a single "latest signal". */
	includeListAppearances?: boolean;
}
